import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { GREY } from "@/config/mui/colorPalette";
import { CommunicationState } from "@/hooks/appointments/useApptCustomizedCommunication";
import useFeatureFlags from "@/hooks/common/useFeatureFlags";
import useFeatureManagement from "@/hooks/common/useFeatureManagement";
import ExpandableSelectableSection, {
  CheckboxState,
} from "../serviceFlow/settings/workforce/common/expandableSelectableSection";

type CommunicationKey = keyof CommunicationState;

type CommunicationOverrideModalProps = {
  onCancel: () => void;
  onSave: () => void;
  clientName: string;
  communicationState: CommunicationState;
  setCommunicationState: Dispatch<SetStateAction<CommunicationState>>;
  editableChannels?: Record<CommunicationKey, boolean>;
  isOpen: boolean;
};

type CommunicationSection = {
  title: string;
  key: CommunicationKey;
};

export default function CommunicationOverrideModal({
  onCancel,
  clientName,
  communicationState,
  setCommunicationState,
  onSave,
  editableChannels,
  isOpen,
}: CommunicationOverrideModalProps) {
  const { checkInNotificationsV1Enabled } = useFeatureFlags();
  const { checkInAlertsEnabled } = useFeatureManagement();

  const initialCommunicationSections: CommunicationSection[] = [
    {
      title: "On booking appointment info",
      key: "sendBookingConfirmation",
    },
    {
      title: "Confirmation & reminder",
      key: "sendConfirmationReminder",
    },
    {
      title: "Day of appointment reminder",
      key: "sendSecondReminder",
    },
    {
      title: "Immediately after the appointment",
      key: "sendAfterAppointmentNote",
    },
    {
      title: "Immediately after the payment (receipt)",
      key: "sendAfterPaymentReceipt",
    },
    {
      title: "Follow-up note",
      key: "sendFollowupNote",
    },
  ] as const;

  const includeCheckInNotification =
    checkInNotificationsV1Enabled && checkInAlertsEnabled;

  const communicationSections: CommunicationSection[] =
    includeCheckInNotification
      ? [
          ...initialCommunicationSections.slice(0, 3),
          {
            title: "Check-in notification",
            key: "sendCheckInReminder",
          },
          ...initialCommunicationSections.slice(3),
        ]
      : initialCommunicationSections;

  const isChannelEditable = (channel: CommunicationKey) => {
    return editableChannels ? editableChannels[channel] : true;
  };

  const handleToggleAll = (enabled: boolean) => {
    setCommunicationState((prev) => {
      const newState = { ...prev };

      (Object.keys(newState) as Array<CommunicationKey>).forEach((channel) => {
        if (isChannelEditable(channel)) {
          newState[channel] = {
            sms: enabled,
            mail: enabled,
          };
        }
      });

      return newState;
    });
  };

  const hasAnySelected = (
    Object.keys(communicationState) as Array<CommunicationKey>
  ).some(
    (channel) =>
      (communicationState[channel].sms || communicationState[channel].mail) &&
      isChannelEditable(channel)
  );

  const handleSetApptCommunicationState = (
    key: CommunicationKey,
    type: "sms" | "mail",
    value: boolean
  ) => {
    setCommunicationState((prev) => ({
      ...prev,
      [key]: { ...prev[key], [type]: value },
    }));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      PaperProps={{
        sx: {
          borderRadius: 4,
        },
      }}
    >
      <DialogContent sx={{ py: 0, px: 2 }}>
        <DialogTitle sx={{ pl: 0 }} variant="h2">
          Edit client comms for appointment
        </DialogTitle>
        <Typography variant="body1" color={GREY[90]}>
          Below are all the communications {clientName} will receive about this
          appointment
        </Typography>
        <Stack direction="row" sx={{ my: 2 }}>
          <Button
            sx={{
              pl: 0,
            }}
            onClick={() => handleToggleAll(!hasAnySelected)}
          >
            {hasAnySelected ? "Deselect all" : "Select all"}
          </Button>
        </Stack>
        <Stack
          sx={{
            gap: 1,
            mb: 3,
          }}
        >
          {communicationSections.map((section) => (
            <CommunicationOverrideSection
              key={section.key}
              title={section.title}
              disabled={!isChannelEditable(section.key)}
              onChange={(type, value) =>
                handleSetApptCommunicationState(section.key, type, value)
              }
              state={communicationState[section.key]}
            />
          ))}
        </Stack>

        <DialogActions>
          <Button variant="outlined" onClick={onCancel} sx={{ width: "100%" }}>
            Cancel
          </Button>
          <Button onClick={onSave} variant="contained" sx={{ width: "100%" }}>
            Save
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

type CommunicationOverrideSectionProps = {
  title: string;
  onChange: (type: "sms" | "mail", value: boolean) => void;
  disabled?: boolean;
  state: {
    sms: boolean;
    mail: boolean;
  };
};

const CommunicationOverrideSection = ({
  title,
  onChange,
  state,
  disabled,
}: CommunicationOverrideSectionProps) => {
  const getState = () => {
    if (state.mail && state.sms) {
      return CheckboxState.CHECKED;
    }

    if (state.mail || state.sms) {
      return CheckboxState.INDETERMINATE;
    }

    return CheckboxState.UNCHECKED;
  };

  const handleStateChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.name as "sms" | "mail", e.target.checked);
  };

  const handleToggleAll = () => {
    const areBothChecked = state.sms && state.mail;
    onChange("sms", !areBothChecked);
    onChange("mail", !areBothChecked);
  };

  return (
    <ExpandableSelectableSection
      title={title}
      onChange={handleToggleAll}
      state={getState()}
      disabled={disabled}
      tooltip={
        disabled ? "This notification cannot be changed at this stage" : ""
      }
    >
      <Stack
        sx={{
          pl: 5,
          backgroundColor: GREY[5],
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={!!state.mail}
              onChange={handleStateChange}
              name="mail"
              disabled={disabled}
            />
          }
          label="Email"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!state.sms}
              onChange={handleStateChange}
              name="sms"
              disabled={disabled}
            />
          }
          label="SMS"
        />
      </Stack>
    </ExpandableSelectableSection>
  );
};
